import React, { useState, useCallback } from 'react'

import { Lead } from '../../../../models/Outreach/types'
import { LeadsFilters } from '../../../../hooks/Outreach/useLeads'
import { LeadsStats } from './LeadsStats'
import { LeadsSearchAndFilter } from './LeadsSearchAndFilter'
import { LeadsTable } from './LeadsTable'
import { LeadDetailDialog } from './LeadDetailDialog'
import { Container } from '../../../../components/Container/Container'

interface LeadsTabProps {
    leads: Lead[]
    leadsPagination: {
        count: number
        next: string | null
        previous: string | null
        currentPage: number
        pageSize: number
        totalPages: number
        summary_stats: {
            total_count: number
            from_email: number
            from_phone: number
            from_property: number
        } | null
    }
    isLoadingLeads: boolean
    filters: LeadsFilters
    onFiltersChange: (filters: LeadsFilters) => void
    syncLead: (leadId: number) => void
    approveApplication: (leadId: number) => Promise<Lead>
    isApprovingApplication: boolean
    refetchLeads: () => void
}

export const LeadsTab: React.FC<LeadsTabProps> = ({
    leads,
    leadsPagination,
    isLoadingLeads,
    filters,
    onFiltersChange,
    syncLead,
    approveApplication,
    isApprovingApplication,
    refetchLeads,
}) => {
    const [selectedLead, setSelectedLead] = useState<Lead | null>(null)
    const [dialogOpen, setDialogOpen] = useState(false)

    const handleRowClick = (lead: Lead) => {
        setSelectedLead(lead)
        setDialogOpen(true)
    }

    const handleCloseDialog = () => {
        setDialogOpen(false)
        setSelectedLead(null)
    }
    const handleSearchChange = (value: string) => {
        onFiltersChange({ ...filters, search: value, page: 1 })
    }

    const handleClearFilters = () => {
        onFiltersChange({
            page: 1,
            page_size: 25,
        })
    }

    const handlePageChange = (_event: unknown, newPage: number) => {
        onFiltersChange({ ...filters, page: newPage + 1 })
    }

    const handleRowsPerPageChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        onFiltersChange({
            ...filters,
            page_size: parseInt(event.target.value, 10),
            page: 1,
        })
    }

    const handleApproveApplication = useCallback(
        async (leadId: number) => {
            const updated = await approveApplication(leadId)
            setSelectedLead(updated)
            refetchLeads()
        },
        [approveApplication, refetchLeads],
    )

    return (
        <Container style={{ flex: 1, flexDirection: 'column' }}>
            <LeadsStats
                leads={leads}
                leadsPagination={leadsPagination}
                isLoading={isLoadingLeads}
            />
            <LeadsSearchAndFilter
                filters={filters}
                onSearchChange={handleSearchChange}
                onClearFilters={handleClearFilters}
            />
            <LeadsTable
                leads={leads}
                pagination={leadsPagination}
                isLoading={isLoadingLeads}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                onRowClick={handleRowClick}
                syncLead={syncLead}
            />
            <LeadDetailDialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                lead={selectedLead}
                onApproveApplication={handleApproveApplication}
                isApprovingApplication={isApprovingApplication}
            />
        </Container>
    )
}
